import React, { useState, useMemo, useEffect } from "react";
import { styled } from '@mui/material/styles';
import { Switch, Slider, Typography, Box } from '@mui/material';
import { useSelector } from "react-redux";

const ZoneControl = ({ zoneid, lamps = [], onFilterChange }) => {
  const [isOn, setIsOn] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [dimming, setDimming] = useState(0);

  const user = useSelector(state => state.auth.user);

  console.log(`user: ${user}`);

  // 🔥 STATISTIKA
  const stats = useMemo(() => {
    const total = lamps.length;
    const active = lamps.filter(l => l.status === "active").length;
    const inactive = lamps.filter(l => l.status === "inactive").length;
    const alarm = lamps.filter(l => l.status === "alarm").length;

    return { total, active, inactive, alarm };
  }, [lamps]);

  // 🔥 AUTO ZERO AKO NEMA AKTIVNIH
  useEffect(() => {
    const activeLamps = lamps.filter(l => l.status === "active");

    if (activeLamps.length === 0) {
      setIsOn(false);
      setDimming(0);
      return;
    }

    // uzmi dimming vrijednosti
    const values = activeLamps.map(l => Number(l.currentDimming) || 0);

    // prosjek
    const avg =
      values.reduce((sum, v) => sum + v, 0) / values.length;

    setIsOn(true);
    setDimming(Math.round(avg));
  }, [lamps]);

  const sendCommand = async (command) => {
    try {
      setDisabled(true);

      const payload = {
        user: user,
        zoneid,
        command,
      };

      if (command === "on") {
        payload.dimming = dimming;
      }

      await fetch(
        "https://commandserver-smartlight.apps.okd-prod.smart-iot.bhtelecom.ba/api/zonecontrol",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        }
      );

      if (command === "off") {
        setIsOn(false);
        setDimming(0);
      } else if (command === "on") {
        setIsOn(true);
        setDimming(100);
      } else if (!isNaN(command)) {
        setIsOn(true);
        setDimming(Number(command));
      }

    } catch (e) {
      console.error(e);
    } finally {
      setTimeout(() => setDisabled(false), 600);
    }
  };

  const BigSwitch = styled(Switch)(() => ({
    width: 60,
    height: 34,
    padding: 0,
    '& .MuiSwitch-switchBase': {
      padding: 2,
      '&.Mui-checked': {
        transform: 'translateX(26px)',
        color: '#ff9800',
        '& + .MuiSwitch-track': {
          backgroundColor: '#4CAF50',
        },
      },
    },
    '& .MuiSwitch-thumb': {
      width: 28,
      height: 28,
      backgroundColor: '#ff9800',
    },
    '& .MuiSwitch-track': {
      borderRadius: 17,
      backgroundColor: '#ff9800',
    },
  }));

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        minWidth: "360px",
        p: 3,
        borderRadius: "18px",
        backgroundColor: '#0f172a',
        border: '1px solid #3949ab55',

        boxShadow:
          isOn
            ? `
              0 0 ${10 + dimming * 0.3}px rgba(250,204,21,${0.3 + dimming / 200}),
              0 0 ${20 + dimming * 0.5}px rgba(250,204,21,${0.15 + dimming / 250})
            `
            : "0 6px 30px rgba(0,0,0,0.6)",

        transition: "all 0.3s ease"
      }}
    >

      {/* HEADER */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography sx={{ color: "#ff9800", fontSize: "16px", fontWeight: 600 }}>
          Zone Control
        </Typography>

        <BigSwitch
          checked={isOn}
          disabled={disabled}
          onChange={(e) => {
            const checked = e.target.checked;
            setIsOn(checked);

            if (checked) {
              setDimming(100);
              sendCommand("on");
            } else {
              setDimming(0);
              sendCommand("off");
            }
          }}
        />
      </Box>

      {/* STATISTIKA */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>

        {[
          { label: "Ukupno", value: stats.total, color: "#ff9800", key: "all" },
          { label: "Uključeno", value: stats.active, color: "#facc15", key: "active" },
          { label: "Isključeno", value: stats.inactive, color: "#6b7280", key: "inactive" },
          { label: "Pod alarmom", value: stats.alarm, color: "#dc2626", key: "alarm" }
        ].map(item => (
          <Box
            key={item.key}
            onClick={() => onFilterChange?.(item.key)}
            sx={{
              textAlign: "center",
              cursor: "pointer",
              px: 1,
              py: 0.5,
              borderRadius: "8px",
              transition: "0.2s",
              '&:hover': {
                backgroundColor: '#1e293b'
              }
            }}
          >
            <Typography sx={{ color: "#94a3b8", fontSize: "11px" }}>
              {item.label}
            </Typography>

            <Typography sx={{ color: item.color, fontSize: "16px", fontWeight: 600 }}>
              {item.value}
            </Typography>
          </Box>
        ))}

      </Box>

      {/* DIMMING */}
      <Box>
        <Typography sx={{ color: "#ff9800", fontSize: "13px", mb: 1 }}>
          Zone Power: {dimming}%
        </Typography>

        <Slider
          value={dimming}
          onChange={(e, v) => setDimming(v)}
          onChangeCommitted={(e, v) => sendCommand(v.toString())}
          disabled={disabled || !isOn}
          min={0}
          max={100}
          step={1}
          size="medium"
          sx={{
            color: isOn
              ? `rgba(250,204,21,${0.5 + dimming / 200})`
              : "#6b7280"
          }}
        />
      </Box>

    </Box>
  );
};

export default ZoneControl;